/**
 * Cookie Consent Banner
 * 
 * Prominent modal-style overlay displayed for first-time visitors.
 * Provides equal prominence to Accept All and Reject All options (GDPR compliant).
 * User can still interact with the site but the overlay encourages a decision.
 */

import { useTranslation } from 'react-i18next';
import { useConsent } from '../../contexts/ConsentContext';
import { Cookie, Settings, Shield, Check } from 'lucide-react';

export function CookieBanner() {
  const { t } = useTranslation('common');
  const { hasDecided, acceptAll, rejectAll, openModal } = useConsent();

  // Don't show if user has already made a decision
  if (hasDecided) {
    return null;
  }

  return (
    <>
      {/* Semi-transparent overlay - allows clicking through but dims content */}
      <div 
        className="cookie-overlay fixed inset-0 z-40 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300"
        aria-hidden="true"
      />
      
      {/* Centered modal dialog */}
      <div className="cookie-banner fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="cookie-banner-modal pointer-events-auto w-full max-w-lg bg-card border border-border rounded-xl shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-banner-title"
        >
          {/* Header */}
          <div className="cookie-banner-header p-6 pb-4 text-center">
            <div className="cookie-banner-icon mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Cookie className="w-7 h-7 text-primary" />
            </div>
            <h2 
              id="cookie-banner-title"
              className="cookie-banner-title text-xl font-semibold text-foreground mb-2"
            >
              {t('consent.banner.title', 'We value your privacy')}
            </h2>
            <p className="cookie-banner-description text-sm text-muted-foreground leading-relaxed">
              {t('consent.banner.description', 'We use cookies to improve your experience, analyze site traffic, and show personalized content. You can accept all cookies, reject non-essential ones, or customize your preferences.')}
            </p>
          </div>

          {/* Actions */}
          <div className="cookie-banner-actions p-6 pt-2 space-y-3">
            {/* Primary actions row - Accept and Reject with equal prominence */}
            <div className="cookie-banner-primary-actions flex flex-col sm:flex-row gap-3">
              {/* Reject All button - GDPR requires equal prominence */}
              <button
                onClick={rejectAll}
                className="cookie-banner-reject-btn flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium text-foreground bg-secondary border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Shield className="w-4 h-4" />
                {t('consent.banner.rejectAll', 'Reject All')}
              </button>

              {/* Accept All button */}
              <button
                onClick={acceptAll}
                className="cookie-banner-accept-btn flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Check className="w-4 h-4" />
                {t('consent.banner.acceptAll', 'Accept All')}
              </button>
            </div>

            {/* Secondary action - Customize */}
            <button
              onClick={openModal}
              className="cookie-banner-customize-btn w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-muted-foreground bg-transparent border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Settings className="w-4 h-4" />
              {t('consent.banner.customize', 'Customize preferences')}
            </button>

            {/* Policy link */}
            <p className="cookie-banner-footer text-center text-xs text-muted-foreground pt-2">
              {t('consent.banner.policyNote', 'By using our site, you acknowledge our')}{' '}
              <a 
                href="/legal/cookies/" 
                className="cookie-banner-policy-link text-primary hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('consent.banner.cookiePolicy', 'Cookie Policy')}
              </a>
              {' '}{t('consent.banner.and', 'and')}{' '}
              <a 
                href="/legal/privacy/" 
                className="cookie-banner-policy-link text-primary hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('consent.banner.privacyPolicy', 'Privacy Policy')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

